import type { ConfirmationTokenResult } from '@stripe/stripe-js';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { isCancellationPolicyApplicable } from '@shared/reservations/reservationUtil';
import { CONFLICT } from '@shared/statusCodes';
import { CANCELLATION_POLICY_HAS_CHANGED_ERROR_MESSAGE } from 'webReservations/errors/messages';
import { getRestaurantDetailsById } from 'webReservations/restaurant/apiHelpers';
import { useOfferCheckoutContext } from '../context/OfferCheckoutContext';
import { useRestaurantContext } from '../context/RestaurantContext';
import { OFFER_CHECKOUT_CONFIRMATION_TERMINAL_PATH } from '../paths';
import { createOffer } from './apiHelpers';
import { enqueueGuestOfferPricingConversion } from 'webReservations/pricingAnalytics/guestPricingConversionReporting';
import { sendGa4SendOfferEvent } from './gaHelpers';
import { usePaymentMethods } from './usePaymentMethods';

interface UseOfferCheckoutOptions {
  /**
   * Payment Element flow (createConfirmationToken). Required when saving a new
   * card. Skipped when a saved selectedPaymentMethodId is set.
   */
  processConfirmationToken?: (
    event?: FormEvent,
  ) => Promise<ConfirmationTokenResult | undefined>;
}

export const useOfferCheckout = (
  offerAmount: number,
  getConfirmationPath?: (offerId: string) => string,
  options?: UseOfferCheckoutOptions,
) => {
  const navigate = useNavigate();
  const { restaurantDetails, setRestaurantDetails } = useRestaurantContext();
  const {
    offerCheckout: { date, guestCount, listingId, publicName, time },
  } = useOfferCheckoutContext();

  const {
    processPaymentMethod,
    selectedPaymentMethodId,
    setupFutureCardUsage,
    handleOnError,
    isLoading,
    paymentMethods,
    handleOnChangePaymentMethod,
    handleOnChangeName,
    handleOnChangeCardElement,
    handleOnChangeSaveCard,
    handleOnDeletePaymentMethod,
    checkoutResponseError,
    isPayButtonDisabled,
  } = usePaymentMethods();

  const handleOnClickSendOffer = async (event?: FormEvent) => {
    event?.preventDefault();

    const { cancellationPolicy } = restaurantDetails;
    const hasApplicableCancellationPolicy =
      !!cancellationPolicy &&
      isCancellationPolicyApplicable(offerAmount, cancellationPolicy.threshold);

    let paymentMethodId = selectedPaymentMethodId || '';
    let confirmationTokenId = '';
    let shouldSetupFutureCardUsage = setupFutureCardUsage;

    try {
      if (!selectedPaymentMethodId && options?.processConfirmationToken) {
        const confirmationTokenResult =
          await options.processConfirmationToken(event);
        confirmationTokenId =
          confirmationTokenResult?.confirmationToken?.id ?? '';
        shouldSetupFutureCardUsage =
          confirmationTokenResult?.confirmationToken?.setup_future_usage ===
          'off_session';
      } else if (!selectedPaymentMethodId) {
        const newPaymentMethod = await processPaymentMethod(event);
        paymentMethodId = newPaymentMethod?.paymentMethod?.id ?? '';
      }
    } catch (e) {
      handleOnError(e instanceof Error ? e.message : 'something went wrong');
      reportAppError(e);
      return;
    }

    if (!paymentMethodId && !confirmationTokenId) return;

    try {
      const createOfferResponse = await createOffer({
        date,
        expectedCancellationPolicyId: hasApplicableCancellationPolicy
          ? cancellationPolicy.id
          : null,
        guestCount,
        listingId,
        offerAmount,
        ...(confirmationTokenId
          ? { confirmationTokenId }
          : { paymentMethodId }),
        setupFutureCardUsage: shouldSetupFutureCardUsage,
        time,
      });
      enqueueGuestOfferPricingConversion({
        restaurantId: restaurantDetails.id,
        offerId: createOfferResponse.id,
      });

      navigate(
        getConfirmationPath
          ? getConfirmationPath(createOfferResponse.id)
          : OFFER_CHECKOUT_CONFIRMATION_TERMINAL_PATH,
        {
          replace: true,
          state: {
            date,
            fee: createOfferResponse.fee,
            guestCount,
            offerAmount: createOfferResponse.price,
            publicName,
            tax: createOfferResponse.tax,
            time,
            total: createOfferResponse.total,
          },
        },
      );

      sendGa4SendOfferEvent({
        date,
        offerAmount: createOfferResponse.price,
        restaurantName: restaurantDetails.name,
        publicName,
        time,
      });
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.statusCode === CONFLICT) {
          try {
            const restaurantDetailsResponse = await getRestaurantDetailsById(
              restaurantDetails.id,
            );
            setRestaurantDetails(restaurantDetailsResponse);
            handleOnError(CANCELLATION_POLICY_HAS_CHANGED_ERROR_MESSAGE);
            return;
          } catch (restaurantDetailsError) {
            handleOnError('something went wrong');
            reportAppError(restaurantDetailsError);
            return;
          }
        }
        handleOnError('something went wrong');
        reportAppError(e);
        return;
      }
      handleOnError('something went wrong');
      reportAppError(e);
    }
  };

  return {
    handleOnClickSendOffer,
    paymentMethods,
    selectedPaymentMethodId,
    setupFutureCardUsage,
    handleOnChangePaymentMethod,
    handleOnChangeName,
    handleOnChangeCardElement,
    handleOnChangeSaveCard,
    handleOnDeletePaymentMethod,
    isLoading,
    isPayButtonDisabled,
    checkoutResponseError,
  };
};
